import { useState, useEffect } from "react"
import { Select, Checkbox } from "antd"
import Button from "../../../../atoms/Button/Button"
import Input from "../../../../atoms/Input/Input"
import { Label } from "../../../../atoms/Label/Label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../atoms/Card/Card"
import Badge from "../../../../atoms/Badge/Badge"
import { Separator } from "../../../../atoms/Separator/Separator"
import editIcon from "../../../../assets/icons/edit.svg"
import { useHealthProfileCalculations } from "../../hooks/useHealthProfileCalculations"
import { useCalorieCalculation } from "../../hooks/useCalorieCalculation"
import { 
  Step1Data, 
  ComputedResults 
} from "../../types"
import "./HealthProfileStep.scss"

interface HealthProfileStepProps {
  initialData: Partial<Step1Data>
  onComplete: (data: Step1Data, computed: ComputedResults) => void
  onDataUpdate?: (data: Partial<Step1Data>) => void
  isCompleted?: boolean
  onEdit?: () => void
  apiData?: any
}

interface EditableTargets {
  calories: number
  carbs: number
  protein: number
  fats: number
}

interface EditableMeal {
  calories: number
  reason: string
}

const INITIAL_FORM_DATA = {
  demographics: {
    gender: "M" as const,
    age: 25,
    height_cm: 170,
    weight_kg: 70,
  },
  conditions: [] as string[],
  goal: {
    type: "maintain" as const,
    rate_per_week: 0.5,
    macro_split_pct: { carbs: 50, protein: 25, fats: 25 },
  },
  behavior_signals: {
    late_sleeper: false,
    irregular_breakfast: false,
    evening_tea_snack: false,
    late_night_hunger: false,
    fruit_inconsistent: false,
    sleep_disturbed: false,
    activity_level: "moderate" as const,
  },
  meal_types: {
    once_awake: false,
    early_morning: false,
    breakfast: true,
    morning_snack: false,
    lunch: true,
    evening_snack: false,
    dinner: true,
    post_dinner: false,
    bed_time: false,
  }
} satisfies Partial<Step1Data>

const BEHAVIOR_OPTIONS = [
  { key: "late_sleeper", label: "Late Sleeper" },
  { key: "irregular_breakfast", label: "Irregular Breakfast" },
  { key: "evening_tea_snack", label: "Evening Tea/Snack" },
  { key: "late_night_hunger", label: "Late Night Hunger" },
  { key: "fruit_inconsistent", label: "Inconsistent Fruit Intake" },
  { key: "sleep_disturbed", label: "Disturbed Sleep" },
]

const MEAL_OPTIONS = [
  { key: "once_awake", label: "Once Awake" },
  // { key: "early_morning", label: "Early Morning" },
  { key: "breakfast", label: "Breakfast" },
  { key: "morning_snack", label: "Morning Snack" },
  { key: "lunch", label: "Lunch" },
  { key: "evening_snack", label: "Evening Snack" },
  { key: "dinner", label: "Dinner" },
  // { key: "post_dinner", label: "Post Dinner" },
  { key: "bed_time", label: "Bed Time" },
]

export function HealthProfileStep({ 
  initialData, 
  onComplete, 
  onDataUpdate, 
  isCompleted, 
  onEdit, 
  apiData 
}: HealthProfileStepProps) {
  const [formData, setFormData] = useState<Partial<Step1Data>>({
    demographics: { 
      gender: initialData.demographics?.gender || INITIAL_FORM_DATA.demographics.gender,
      age: initialData.demographics?.age || INITIAL_FORM_DATA.demographics.age,
      height_cm: initialData.demographics?.height_cm || INITIAL_FORM_DATA.demographics.height_cm,
      weight_kg: initialData.demographics?.weight_kg || INITIAL_FORM_DATA.demographics.weight_kg,
    },
    conditions: initialData.conditions || [],
    goal: { 
      type: initialData.goal?.type || INITIAL_FORM_DATA.goal.type,
      rate_per_week: initialData.goal?.rate_per_week || INITIAL_FORM_DATA.goal.rate_per_week,
      macro_split_pct: initialData.goal?.macro_split_pct || INITIAL_FORM_DATA.goal.macro_split_pct,
      goal_calories: initialData.goal?.goal_calories,
      macro_targets_g: initialData.goal?.macro_targets_g,
    },
    behavior_signals: { 
      late_sleeper: initialData.behavior_signals?.late_sleeper ?? INITIAL_FORM_DATA.behavior_signals.late_sleeper,
      irregular_breakfast: initialData.behavior_signals?.irregular_breakfast ?? INITIAL_FORM_DATA.behavior_signals.irregular_breakfast,
      evening_tea_snack: initialData.behavior_signals?.evening_tea_snack ?? INITIAL_FORM_DATA.behavior_signals.evening_tea_snack,
      late_night_hunger: initialData.behavior_signals?.late_night_hunger ?? INITIAL_FORM_DATA.behavior_signals.late_night_hunger,
      fruit_inconsistent: initialData.behavior_signals?.fruit_inconsistent ?? INITIAL_FORM_DATA.behavior_signals.fruit_inconsistent,
      sleep_disturbed: initialData.behavior_signals?.sleep_disturbed ?? INITIAL_FORM_DATA.behavior_signals.sleep_disturbed,
      activity_level: initialData.behavior_signals?.activity_level || INITIAL_FORM_DATA.behavior_signals.activity_level,
    },
    meal_types: { 
      once_awake: initialData.meal_types?.once_awake ?? INITIAL_FORM_DATA.meal_types.once_awake,
      early_morning: initialData.meal_types?.early_morning ?? INITIAL_FORM_DATA.meal_types.early_morning,
      breakfast: initialData.meal_types?.breakfast ?? INITIAL_FORM_DATA.meal_types.breakfast,
      morning_snack: initialData.meal_types?.morning_snack ?? INITIAL_FORM_DATA.meal_types.morning_snack,
      lunch: initialData.meal_types?.lunch ?? INITIAL_FORM_DATA.meal_types.lunch,
      evening_snack: initialData.meal_types?.evening_snack ?? INITIAL_FORM_DATA.meal_types.evening_snack,
      dinner: initialData.meal_types?.dinner ?? INITIAL_FORM_DATA.meal_types.dinner,
      post_dinner: initialData.meal_types?.post_dinner ?? INITIAL_FORM_DATA.meal_types.post_dinner,
      bed_time: initialData.meal_types?.bed_time ?? INITIAL_FORM_DATA.meal_types.bed_time,
    },
  })

  const [newCondition, setNewCondition] = useState("")
  const [showResults, setShowResults] = useState(false)
  const [isFormInitialized, setIsFormInitialized] = useState(false)
  const [isEditingTargets, setIsEditingTargets] = useState(false)
  const [editableTargets, setEditableTargets] = useState<EditableTargets | null>(null)
  const [editableMeals, setEditableMeals] = useState<Record<string, EditableMeal> | null>(null)

  // Use the custom hook for calculations
  const {
    mapApiDataToFormData,
    calculateTotalCalories,
    generateMealReasons,
    distributeMealCalories,
    constants,
  } = useHealthProfileCalculations(formData, editableTargets, apiData)

  // calorie calculation hook
  const {
    calculatedValues,
    recalculate,
  } = useCalorieCalculation({
    demographics: formData.demographics || {
      gender: "M",
      age: 25,
      height_cm: 170,
      weight_kg: 70
    },
    behaviorSignals: formData.behavior_signals || {
      late_sleeper: false,
      irregular_breakfast: false,
      evening_tea_snack: false,
      late_night_hunger: false,
      fruit_inconsistent: false,
      sleep_disturbed: false,
      activity_level: "moderate"
    },
    goal: formData.goal || {
      type: "maintain",
      macro_split_pct: { carbs: 50, protein: 25, fats: 25 }
    },
    macroSplitPct: formData.goal?.macro_split_pct || { carbs: 50, protein: 25, fats: 25 },
    mealTypes: formData.meal_types || {
      once_awake: false,
      early_morning: false,
      breakfast: true,
      morning_snack: false,
      lunch: true,
      evening_snack: false,
      dinner: true,
      post_dinner: false,
      bed_time: false
    },
    isFormInitialized,
    isEditingTargets
  })

  // Notify parent component of data changes
  useEffect(() => {
    if (onDataUpdate && isFormInitialized) {
      onDataUpdate(formData)
    }
  }, [formData, onDataUpdate, isFormInitialized])

  // Auto-update form data when calculated values change
  useEffect(() => {
    if (!calculatedValues || !isFormInitialized || isEditingTargets) return

    const { adjustedCalories, macros, mealDistribution } = calculatedValues

    // Update form data with new calculations
    setFormData(prev => ({
      ...prev,
      goal: {
        ...prev.goal!,
        goal_calories: adjustedCalories,
        macro_targets_g: macros,
      },
      total_calorie_goal: adjustedCalories,
      macro_targets_g: macros,
      per_meal_kcal: mealDistribution,
    }))

    // Show results if not already shown
    if (!showResults) {
      setShowResults(true)
    }
  }, [calculatedValues, isFormInitialized, isEditingTargets, showResults])

  // Initialize form data from API or initial data
  useEffect(() => {
    if (isFormInitialized) return
    
    if (apiData) {
      const mappedData = mapApiDataToFormData(apiData)
      if (mappedData) {
        setFormData(mappedData)
        setIsFormInitialized(true)
        
        if (mappedData.goal?.goal_calories && mappedData.goal?.macro_targets_g) {
          setEditableTargets({
            calories: mappedData.goal.goal_calories,
            carbs: mappedData.goal.macro_targets_g.carbs,
            protein: mappedData.goal.macro_targets_g.protein,
            fats: mappedData.goal.macro_targets_g.fats,
          })
          setShowResults(true)
        }
        if (mappedData.total_calorie_goal || mappedData.macro_targets_g || mappedData.per_meal_kcal) {
          setFormData(prev => ({
            ...prev,
            total_calorie_goal: mappedData.total_calorie_goal,
            macro_targets_g: mappedData.macro_targets_g,
            meal_type_reasons: mappedData.meal_type_reasons,
            per_meal_kcal: mappedData.per_meal_kcal,
          }))
        }
        return
      }
    }

    if (initialData && Object.keys(initialData).length > 0) {
      setFormData((prevData: any) => ({
        demographics: { ...prevData.demographics, ...initialData.demographics },
        conditions: initialData.conditions || prevData.conditions,
        goal: { ...prevData.goal, ...initialData.goal },
        behavior_signals: { ...prevData.behavior_signals, ...initialData.behavior_signals },
        meal_types: { ...prevData.meal_types, ...initialData.meal_types },
      }))
      setIsFormInitialized(true)

      if (initialData.goal?.goal_calories && initialData.goal?.macro_targets_g) {
        setEditableTargets({
          calories: initialData.goal.goal_calories,
          carbs: initialData.goal.macro_targets_g.carbs,
          protein: initialData.goal.macro_targets_g.protein,
          fats: initialData.goal.macro_targets_g.fats,
        })
        setShowResults(true)
      }
      if (initialData.total_calorie_goal || initialData.macro_targets_g || initialData.per_meal_kcal) {
        setFormData(prev => ({
          ...prev,
          total_calorie_goal: initialData.total_calorie_goal,
          macro_targets_g: initialData.macro_targets_g,
          meal_type_reasons: initialData.meal_type_reasons,
          per_meal_kcal: initialData.per_meal_kcal,
        }))
      }
    }
  }, [initialData, apiData, mapApiDataToFormData, isFormInitialized])

  const handleCalculate = () => {
    recalculate()
    setShowResults(true)
  }

  const handleEditTargets = () => {
    const currentCalories = formData.goal?.goal_calories || calculatedValues?.adjustedCalories || calculateTotalCalories()
    const currentMacros = formData.goal?.macro_targets_g || calculatedValues?.macros || {
      carbs: Math.round((currentCalories * 50) / 100 / 4),
      protein: Math.round((currentCalories * 25) / 100 / 4),
      fats: Math.round((currentCalories * 25) / 100 / 9),
    }
    
    // Use calculated meal distribution or fallback to old method
    const mealCalories = calculatedValues?.mealDistribution || distributeMealCalories(currentCalories)
    const mealReasons = formData.meal_type_reasons || generateMealReasons()

    setEditableTargets({
      calories: currentCalories,
      carbs: currentMacros.carbs,
      protein: currentMacros.protein,
      fats: currentMacros.fats,
    })

    // Initialize editable meals
    const formattedMeals: Record<string, EditableMeal> = {}
    Object.entries(mealCalories).forEach(([meal, calories]) => {
      if (calories > 0) { // Only include meals with calories
        formattedMeals[meal] = {
          calories: calories,
          reason: mealReasons[meal] || ''
        }
      }
    })
    setEditableMeals(formattedMeals)
    
    setIsEditingTargets(true)
  }

  const handleSaveTargets = () => {
    if (editableTargets) {
      // Validate that calories are within the valid range (800-5000)
      if (editableTargets.calories < 800 || editableTargets.calories > 5000) {
        alert("Total calories must be between 800 and 5000. Please adjust the value before saving.")
        return
      }
      
      setFormData(prev => ({
        ...prev,
        goal: {
          ...prev.goal!,
          goal_calories: editableTargets.calories,
          macro_targets_g: {
            carbs: editableTargets.carbs,
            protein: editableTargets.protein,
            fats: editableTargets.fats,
          }
        },
        // Add the computed results to formData so they can be accessed by the API transformation
        total_calorie_goal: editableTargets.calories,
        macro_targets_g: {
          carbs: editableTargets.carbs,
          protein: editableTargets.protein,
          fats: editableTargets.fats,
        },
        // Save meal calories if they exist
        per_meal_kcal: editableMeals ? Object.fromEntries(
          Object.entries(editableMeals).map(([meal, mealData]) => [meal, mealData.calories])
        ) : undefined
      }))
      setIsEditingTargets(false)
    }
  }

  const handleCancelEdit = () => {
    setEditableTargets(null)
    setEditableMeals(null)
    setIsEditingTargets(false)
  }

  const updateEditableTarget = (field: keyof EditableTargets, value: string | number) => {
    if (editableTargets) {
      const numericValue = typeof value === 'string' ? parseFloat(value) : value
      
      setEditableTargets({
        ...editableTargets,
        [field]: numericValue,
      })
    }
  }

  const updateEditableMeal = (meal: string, field: keyof EditableMeal, value: string | number) => {
    if (editableMeals) {
      // Convert string to number for calories field
      const numericValue = field === 'calories' && typeof value === 'string' ? parseFloat(value) : value
      
      setEditableMeals({
        ...editableMeals,
        [meal]: {
          ...editableMeals[meal],
          [field]: numericValue,
        },
      })
    }
  }


  const addCondition = () => {
    if (newCondition.trim()) {
      setFormData((prev: any) => ({
        ...prev,
        conditions: [...(prev.conditions || []), newCondition.trim()],
      }))
      setNewCondition("")
    }
  }

  const removeCondition = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      conditions: prev.conditions?.filter((_: any, i: number) => i !== index) || [],
    }))
  }

  const isFormValid = () => {
    return (
      formData.demographics?.age &&
      formData.demographics?.height_cm &&
      formData.demographics?.weight_kg &&
      Object.values(formData.meal_types || {}).some((enabled) => enabled)
    )
  }


  const renderDemographicsSection = () => (
    <Card className="demographics-section">
      <CardHeader>
        <CardTitle>Demographics</CardTitle>
      </CardHeader>
      <CardContent className="demographics-grid">
        <div>
          <Label htmlFor="gender">Gender</Label>
          <div className="gender-select">
            <Select
              className='ant-select-selection'
              value={formData.demographics?.gender}
              onChange={(value: "M" | "F") =>
                setFormData((prev: any) => ({ ...prev, demographics: { ...prev.demographics!, gender: value } }))
              }
              options={[
                { value: "M", label: "Male" },
                { value: "F", label: "Female" },
              ]}
              placeholder="Select gender"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="age">Age (years)</Label>
          <Input
            type="number"
            min="18"
            max="100"
            value={formData.demographics?.age || ""}
            onChange={(e: any) =>
              setFormData((prev: any) => ({
                ...prev,
                demographics: { ...prev.demographics!, age: Number.parseInt(e.target.value) || 0 },
              }))
            }
            name="age"
            id="age"
            placeholder="Enter age"
          />
        </div>
        <div>
          <Label htmlFor="height">Height (cm)</Label>
          <Input
            type="number"
            min="120"
            max="230"
            value={formData.demographics?.height_cm || ""}
            onChange={(e: any) =>
              setFormData((prev: any) => ({
                ...prev,
                demographics: { ...prev.demographics!, height_cm: Number.parseFloat(e.target.value) || 0 },
              }))
            }
            name="height"
            id="height"
            placeholder="Enter height"
          />
        </div>
        <div>
          <Label htmlFor="weight">Weight (kg)</Label>
          <Input
            type="number"
            min="30"
            max="250"
            value={formData.demographics?.weight_kg || ""}
            onChange={(e: any) =>
              setFormData((prev: any) => ({
                ...prev,
                demographics: { ...prev.demographics!, weight_kg: Number.parseFloat(e.target.value) || 0 },
              }))
            }
            name="weight"
            id="weight"
            placeholder="Enter weight"
          />
        </div>
      </CardContent>
    </Card>
  )

  const renderMedicalConditionsSection = () => (
    <Card className="medical-conditions-section">
      <CardHeader>
        <CardTitle>Medical Conditions (Optional)</CardTitle>
        <CardDescription>Add any relevant medical conditions</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="add-condition-group">
          <div className="input-wrapper">
            <Input
              placeholder="e.g., diabetes, hypertension"
              value={newCondition}
              onChange={(e: any) => setNewCondition(e.target.value)}
              onKeyDown={(e: any) => e.key === "Enter" && addCondition()}
              type="text"
              name="newCondition"
              id="newCondition"
            />
          </div>
          <Button 
            onClick={addCondition} 
            variant="outlined"
            className="add-button"
          >
            Add
          </Button>
        </div>
        <div className="conditions-badges">
          {formData.conditions?.map((condition: string, index: number) => (
            <Badge key={index} variant="secondary" onClick={() => removeCondition(index)}>
              {condition} ×
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  )

  const renderGoalMacrosSection = () => (
    <Card className="goal-macros-section">
      <CardHeader>
        <CardTitle>Goal & Macro Split</CardTitle>
      </CardHeader>
      <CardContent className="goal-content">
        <div className="goal-type-rate-grid">
          <div>
            <Label>Goal Type</Label>
            <div className="goal-type-grid">
              <Select
                className='ant-select-selection'
                value={formData.goal?.type}
                onChange={(value: "loss" | "gain" | "maintain") =>
                  setFormData((prev: any) => ({ ...prev, goal: { ...prev.goal!, type: value } }))
                }
                options={[
                  { value: "loss", label: "Weight Loss" },
                  { value: "gain", label: "Weight Gain" },
                  { value: "maintain", label: "Maintain Weight" },
                ]}
                placeholder="Select goal type"
              />
            </div>
          </div>
          <div>
            <Label>Rate per Week (kg)</Label>
            <div className="rate-per-week-grid">
              <Input
                type="number"
                step="0.1"
                value={formData.goal?.rate_per_week ?? ""}
                onChange={(e: any) => {
                  const value = e.target.value
                  setFormData((prev: any) => ({
                    ...prev,
                    goal: { 
                      ...prev.goal!, 
                      rate_per_week: value === "" ? undefined : Number.parseFloat(value) || 0
                    },
                  }))
                }}
                name="rate_per_week"
                id="rate_per_week"
                placeholder="0.5"
              />
            </div>
          </div>
        </div>
        <div className="macro-split-grid">
          <div>
            <Label>Carbs (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.goal?.macro_split_pct.carbs || ""}
              onChange={(e: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  goal: {
                    ...prev.goal!,
                    macro_split_pct: {
                      ...prev.goal!.macro_split_pct,
                      carbs: Number.parseInt(e.target.value) || 0,
                    },
                  },
                }))
              }
              name="carbs"
              id="carbs"
              placeholder="50"
            />
          </div>
          <div>
            <Label>Protein (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.goal?.macro_split_pct.protein || ""}
              onChange={(e: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  goal: {
                    ...prev.goal!,
                    macro_split_pct: {
                      ...prev.goal!.macro_split_pct,
                      protein: Number.parseInt(e.target.value) || 0,
                    },
                  },
                }))
              }
              name="protein"
              id="protein"
              placeholder="25"
            />
          </div>
          <div>
            <Label>Fats (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.goal?.macro_split_pct.fats || ""}
              onChange={(e: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  goal: {
                    ...prev.goal!,
                    macro_split_pct: {
                      ...prev.goal!.macro_split_pct,
                      fats: Number.parseInt(e.target.value) || 0,
                    },
                  },
                }))
              }
              name="fats"
              id="fats"
              placeholder="25"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )

  const renderBehaviorSignalsSection = () => (
    <Card className="behavior-signals-section">
      <CardHeader>
        <CardTitle>Behavior Signals</CardTitle>
      </CardHeader>
      <CardContent className="behavior-content">
        <div className="behavior-checkboxes">
          {BEHAVIOR_OPTIONS.map(({ key, label }) => (
            <div key={key} className="behavior-item">
              <Checkbox
                checked={(formData.behavior_signals?.[key as keyof typeof formData.behavior_signals] as boolean) || false}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    behavior_signals: { ...prev.behavior_signals!, [key]: e.target.checked },
                  }))
                }
              >
                {label}
              </Checkbox>
            </div>
          ))}
        </div>
        <div className="activity-level-grid">
          <div>
            <Label>Activity Level</Label>
            <Select
              className='ant-select-selection'
              value={formData.behavior_signals?.activity_level}
              onChange={(value: "sedentary" | "light" | "moderate" | "heavy") => {
                setFormData((prev: any) => ({
                  ...prev,
                  behavior_signals: { ...prev.behavior_signals!, activity_level: value },
                }))
              }}
              options={[
                { value: "sedentary", label: "Sedentary (little/no exercise)" },
                { value: "light", label: "Light (light exercise 1-3 days/week)" },
                { value: "moderate", label: "Moderate (moderate exercise 3-5 days/week)" },
                { value: "heavy", label: "Heavy (hard exercise 6-7 days/week)" },
              ]}
              placeholder="Select activity level"
            />
          </div>
          <div></div>
          <div></div>
        </div>
      </CardContent>
    </Card>
  )

  const renderMealTypesSection = () => (
    <Card className="meal-types-section">
      <CardHeader>
        <CardTitle>Available Meal Slots</CardTitle>
        <CardDescription>Select which meals you want in your plan</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="meal-types-grid">
          {MEAL_OPTIONS.map(({ key, label }) => (
            <div key={key} className="meal-item">
              <Checkbox
                checked={formData.meal_types?.[key as keyof typeof formData.meal_types] || false}
                onChange={(e) => {
                  setFormData((prev: any) => {
                    const newData = {
                      ...prev,
                      meal_types: { ...prev.meal_types!, [key]: e.target.checked },
                    }
                    return newData
                  })
                }}
              >
                {label}
              </Checkbox>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )

        const renderTargetsGrid = () => (
    <div className="targets-grid">
      <div className="target-item">
        {!isEditingTargets ? (
          <>
            <div className="target-value">{formData.goal?.goal_calories || calculatedValues?.adjustedCalories || calculateTotalCalories()}</div>
            <div className="target-label">Total Calories</div>
          </>
        ) : (
          <div className="edit-input-group">
            <Input
              type="number"
              min="800"
              max="5000"
              step="50"
              value={editableTargets?.calories }
              onChange={(e: any) => updateEditableTarget("calories", e.target.value )}
              className="edit-input"
              name="editCalories"
              id="editCalories"
              placeholder="800-5000 calories"
            />
            {editableTargets?.calories && editableTargets.calories < 800 && (
              <div className="validation-error">Calories must be at least 800</div>
            )}
            {editableTargets?.calories && editableTargets.calories > 5000 && (
              <div className="validation-error">Calories cannot exceed 5000</div>
            )}
            <div className="target-label">Total Calories</div>
          </div>
        )}
      </div>
      <div className="target-item">
        {!isEditingTargets ? (
          <>
            <div className="target-value">
              {formData.goal?.macro_targets_g?.carbs || calculatedValues?.macros?.carbs || 0}g
            </div>
            <div className="target-label">Carbs</div>
          </>
        ) : (
          <div className="edit-input-group">
            <Input
              type="number"
              min="0"
              step="1"
              value={editableTargets?.carbs }
              onChange={(e: any) => updateEditableTarget("carbs", e.target.value)}
              className="edit-input"
              name="editCarbs"
              id="editCarbs"
              placeholder="Carbs (g)"
            />
            <div className="target-label">Carbs (g)</div>
          </div>
        )}
      </div>
      <div className="target-item">
        {!isEditingTargets ? (
          <>
            <div className="target-value">
              {formData.goal?.macro_targets_g?.protein || calculatedValues?.macros?.protein || 0}g
            </div>
            <div className="target-label">Protein</div>
          </>
        ) : (
          <div className="edit-input-group">
            <Input
              type="number"
              min="0"
              step="1"
              value={editableTargets?.protein }
              onChange={(e: any) => updateEditableTarget("protein", e.target.value )}
              className="edit-input"
              name="editProtein"
              id="editProtein"
              placeholder="Protein (g)"
            />
            <div className="target-label">Protein (g)</div>
          </div>
        )}
      </div>
      <div className="target-item">
        {!isEditingTargets ? (
          <>
            <div className="target-value">
              {formData.goal?.macro_targets_g?.fats || calculatedValues?.macros?.fats || 0}g
            </div>
            <div className="target-label">Fats</div>
          </>
        ) : (
          <div className="edit-input-group">
            <Input
              type="number"
              min="0"
              step="1"
              value={editableTargets?.fats }
              onChange={(e: any) => updateEditableTarget("fats",(e.target.value))}
              className="edit-input"
              name="editFats"
              id="editFats"
              placeholder="Fats (g)"
            />
            <div className="target-label">Fats (g)</div>
          </div>
        )}
      </div>
    </div>
  )

  const renderMealDistribution = () => {
    // Get meal distribution from calculated values or fallback to old method
    const mealDistribution = calculatedValues?.mealDistribution || formData.per_meal_kcal || {}
    const mealReasons = formData.meal_type_reasons || generateMealReasons()
    
    // Only show meals that are selected and have calories
    const selectedMeals = Object.entries(mealDistribution).filter(([_, calories]) => calories > 0)
    
    // Calculate total calories for verification
    const totalDistributedCalories = selectedMeals.reduce((sum, [_, calories]) => sum + calories, 0)
    
    return (
      <div className="meal-distribution">
        <h4 className="distribution-title">Meal Distribution & Reasoning</h4>
        {totalDistributedCalories > 0 && (
          <div className="distribution-summary">
            <small>Total Distributed: {totalDistributedCalories} kcal</small>
          </div>
        )}
        <div className="distribution-list">
          {selectedMeals.map(([meal, calories]) => {
            const reason = mealReasons[meal] || 'Meal included in your diet plan'
            const displayCalories = editableMeals?.[meal]?.calories || calories
            
            return (
              <div key={meal} className="distribution-item">
                <div className="meal-info">
                  <div className="meal-name">
                    {constants?.MEAL_DISPLAY_NAMES[meal] || meal}
                  </div>
                  <div className="meal-reason">{reason}</div>
                </div>
                <div className="meal-calories">
                  {!isEditingTargets ? (
                    <div className="calories-value">{displayCalories} kcal</div>
                  ) : (
                    <div className="edit-input-group">
                      <Input
                        type="number"
                        min="0"
                        step="10"
                        value={editableMeals?.[meal]?.calories || calories}
                        onChange={(e: any) => updateEditableMeal(meal, "calories", e.target.value)}
                        className="edit-input"
                        name={`editMealCalories_${meal}`}
                        id={`editMealCalories_${meal}`}
                        placeholder="Calories"
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderResultsSection = () => (
    <Card className="results-section">
      <CardHeader>
        <CardTitle className="results-header">
          Calculated Targets
          {!isEditingTargets ? (
            <button 
              onClick={handleEditTargets} 
              className="edit-button"
              title="Edit targets"
            >
              <img 
                src={editIcon} 
                alt="Edit" 
                className="edit-icon"
              />
            </button>
          ) : (
            <div className="edit-actions">
              <Button variant="outlined" onClick={handleSaveTargets} className="save-button">
                Save
              </Button>
              <Button variant="outlined" onClick={handleCancelEdit} className="cancel-button">
                Cancel
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="results-content">
        {renderTargetsGrid()}
        <Separator />
        {renderMealDistribution()}
      </CardContent>
    </Card>
  )

  return (
    <div className="health-profile-step-container">
      {renderDemographicsSection()}
      {renderMedicalConditionsSection()}
      {renderGoalMacrosSection()}
      {renderBehaviorSignalsSection()}
      {renderMealTypesSection()}

      {!showResults && (
        <div className="calculate-button-container">
          <Button onClick={handleCalculate} disabled={!isFormValid()} className="calculate-button">
            Calculate Targets
          </Button>
        </div>
      )}

      {showResults && renderResultsSection()}
    </div>
  )
}
